import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  LayoutDashboard, Users, Disc3, CalendarDays, Building2, ListMusic, Activity,
  ShieldAlert, Lock, AlertTriangle, ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api from '@/services/api';

// Phase 1 Admin Dashboard overview. Read-only landing that drives every figure from
// EXISTING admin endpoints only: GET /admin/metrics (analytics + data-quality warnings),
// GET /admin/djs?status=pending (review queue) and GET /admin/events (recent catalog).
// It links into the existing /admin/* pages and changes none of them.

// Shared gate visuals — mirrors AdminUsers/AdminDJs/AdminEvents so look & feel match.
function Centered({ children }) {
  return <div className="max-w-4xl mx-auto px-4 py-16 text-center opacity-70">{children}</div>;
}
function GateLogin() {
  return (
    <div className="max-w-xl mx-auto px-4 py-16 text-center">
      <Lock className="mx-auto mb-4 h-10 w-10 opacity-60" />
      <p className="mb-4">Please log in.</p>
      <Link to="/login"><Button>Go to Login</Button></Link>
    </div>
  );
}
function GateAdmin({ role }) {
  return (
    <div className="max-w-xl mx-auto px-4 py-16 text-center">
      <ShieldAlert className="mx-auto mb-4 h-10 w-10 opacity-60" />
      <h1 className="text-xl font-semibold mb-2">Admin access required</h1>
      <p className="opacity-80">Your role is <span className="capitalize font-medium">{role || 'unknown'}</span>.</p>
    </div>
  );
}

// "2026-06-25T20:00:00" / "2026-06-25" -> "2026-06-25 20:00" (no timezone shifting).
const fmtDateTime = (raw) => {
  if (!raw) return '—';
  const s = String(raw);
  return s.slice(0, 16).replace('T', ' ');
};
const fmtPct = (rate) => `${Math.round((Number(rate) || 0) * 100)}%`;

// One metric tile. When `to` is set the whole card is a link into an existing admin page.
function MetricCard({ icon: Icon, label, value, sub, to }) {
  const body = (
    <Card className={to ? 'h-full transition-colors hover:border-primary/50' : 'h-full'}>
      <CardContent className="py-4">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wide opacity-60">{label}</span>
          <Icon className="h-4 w-4 opacity-60" />
        </div>
        <div className="mt-2 text-2xl font-bold leading-none">{value}</div>
        {sub && <div className="mt-1 text-xs opacity-70">{sub}</div>}
      </CardContent>
    </Card>
  );
  return to ? <Link to={to} className="block">{body}</Link> : body;
}

function QuickAction({ icon: Icon, title, sub, to }) {
  return (
    <Link to={to} className="block">
      <Card className="h-full transition-colors hover:border-primary/50">
        <CardContent className="py-4 flex items-center gap-3">
          <span className="shrink-0 w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
            <Icon className="h-5 w-5" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block font-semibold leading-tight">{title}</span>
            <span className="block text-xs opacity-70 truncate">{sub}</span>
          </span>
          <ChevronRight className="h-4 w-4 opacity-50 shrink-0" />
        </CardContent>
      </Card>
    </Link>
  );
}

export default function AdminDashboard() {
  const [authed] = useState(api.isAuthenticated());
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);

  const [metrics, setMetrics] = useState(null);
  const [metricsError, setMetricsError] = useState(false);
  const [pendingDjs, setPendingDjs] = useState([]);
  const [pendingOrgs, setPendingOrgs] = useState([]);
  const [recentEvents, setRecentEvents] = useState([]);

  // Resolve role first (same pattern as the other admin pages).
  useEffect(() => {
    if (!authed) { setLoading(false); return; }
    api.get('/users/me').then((me) => setRole(me.role)).catch(() => {}).finally(() => setLoading(false));
  }, [authed]);

  // Load dashboard data once we know the user is an admin. Each source fails
  // independently so one bad call never blanks the whole page.
  useEffect(() => {
    if (role !== 'admin') return;
    let active = true;

    api.get('/admin/metrics')
      .then((m) => { if (active) { setMetrics(m); setMetricsError(false); } })
      .catch((e) => { if (active) { setMetricsError(true); toast.error(e.message || 'Failed to load metrics'); } });

    api.get('/admin/djs?status=pending')
      .then((rows) => { if (active) setPendingDjs(Array.isArray(rows) ? rows.slice(0, 5) : []); })
      .catch(() => { if (active) setPendingDjs([]); });

    api.get('/admin/organizer-applications?status=pending')
      .then((rows) => { if (active) setPendingOrgs(Array.isArray(rows) ? rows.slice(0, 5) : []); })
      .catch(() => { if (active) setPendingOrgs([]); });

    api.get('/admin/events')
      .then((list) => {
        if (!active) return;
        const arr = Array.isArray(list) ? [...list] : [];
        arr.sort((a, b) => String(b.start_date || '').localeCompare(String(a.start_date || '')));
        setRecentEvents(arr.slice(0, 5));
      })
      .catch(() => { if (active) setRecentEvents([]); });

    return () => { active = false; };
  }, [role]);

  if (!authed) return <GateLogin />;
  if (loading) return <Centered>Loading…</Centered>;
  if (role !== 'admin') return <GateAdmin role={role} />;

  const u = metrics?.users || {};
  const d = metrics?.djs || {};
  const org = metrics?.organizers || {};
  const ev = metrics?.events || {};
  const at = metrics?.attendance || {};
  const ln = metrics?.lineups || {};
  const warnings = Array.isArray(metrics?.warnings) ? metrics.warnings : [];

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* ---- Header ---- */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <LayoutDashboard className="h-6 w-6" /> Admin · Dashboard
        </h1>
        <p className="text-sm opacity-70 mt-1">Platform overview — live figures from your admin APIs.</p>
      </div>

      {metricsError && (
        <Card className="mb-6 border-destructive/40">
          <CardContent className="py-3 text-sm flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            Could not load platform metrics right now. The lists below may still work.
          </CardContent>
        </Card>
      )}

      {/* ---- Metric cards (from /admin/metrics) ---- */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-8">
        <MetricCard icon={Users} label="Users" value={metrics ? (u.total_users ?? 0) : '—'}
          sub={metrics ? `${u.users_with_events ?? 0} with events` : 'loading…'} to="/admin/users" />
        <MetricCard icon={Disc3} label="DJ Applications" value={metrics ? (d.pending_djs ?? 0) : '—'}
          sub={metrics ? `${d.approved_djs ?? 0} approved · ${d.rejected_djs ?? 0} rejected` : 'loading…'} to="/admin/djs" />
        <MetricCard icon={Building2} label="Organizers" value={metrics ? (org.pending_organizers ?? 0) : '—'}
          sub={metrics ? `${org.approved_organizers ?? 0} approved · ${org.rejected_organizers ?? 0} rejected` : 'loading…'} to="/admin/organizers" />
        <MetricCard icon={CalendarDays} label="Events" value={metrics ? (ev.total_events ?? 0) : '—'}
          sub={metrics ? `${ev.events_with_lineups ?? 0} with lineups` : 'loading…'} to="/admin/events" />
        <MetricCard icon={Activity} label="Attendance" value={metrics ? (at.total_attending_events ?? 0) : '—'}
          sub={metrics ? `${at.total_saved_events ?? 0} saved · ${fmtPct(at.attendance_rate)} rate` : 'loading…'} />
        <MetricCard icon={ListMusic} label="Lineups" value={metrics ? (ln.total_lineups ?? 0) : '—'}
          sub={metrics ? `${ln.average_djs_per_lineup ?? 0} DJs/lineup avg` : 'loading…'} />
      </div>

      {/* ---- Quick actions (link into existing admin pages) ---- */}
      <h2 className="text-sm font-semibold uppercase tracking-wide opacity-60 mb-3">Quick actions</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        <QuickAction icon={Disc3} title="Review DJs" sub="Approve, reject, edit applications" to="/admin/djs" />
        <QuickAction icon={CalendarDays} title="Manage Events" sub="Create, edit, publish, archive" to="/admin/events" />
        <QuickAction icon={Building2} title="Review Organizers" sub="Approve, reject, request changes" to="/admin/organizers" />
        <QuickAction icon={Users} title="Users & Roles" sub="Search users, change roles" to="/admin/users" />
      </div>

      {/* ---- Recent pending DJs + recent events ---- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base flex items-center gap-2"><Disc3 className="h-4 w-4" /> Recent pending DJ applications</CardTitle>
            <Link to="/admin/djs" className="text-xs text-primary hover:underline">View all</Link>
          </CardHeader>
          <CardContent className="space-y-2">
            {pendingDjs.length === 0 && <p className="opacity-60 text-sm py-4 text-center">No pending applications.</p>}
            {pendingDjs.map((dj) => (
              <Link key={dj.id} to="/admin/djs" className="block">
                <div className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2 hover:border-primary/50 transition-colors">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{dj.dj_name || '(unnamed)'}</div>
                    <div className="text-xs opacity-70 truncate">{dj.city || '—'} · submitted {fmtDateTime(dj.submitted_at)}</div>
                  </div>
                  <Badge variant="outline" className="shrink-0">Pending</Badge>
                </div>
              </Link>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base flex items-center gap-2"><CalendarDays className="h-4 w-4" /> Recent events</CardTitle>
            <Link to="/admin/events" className="text-xs text-primary hover:underline">View all</Link>
          </CardHeader>
          <CardContent className="space-y-2">
            {recentEvents.length === 0 && <p className="opacity-60 text-sm py-4 text-center">No events yet.</p>}
            {recentEvents.map((e) => (
              <Link key={e.id || e.event_id} to="/admin/events" className="block">
                <div className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2 hover:border-primary/50 transition-colors">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{e.title || '(untitled)'}</div>
                    <div className="text-xs opacity-70 truncate">{[e.venue, e.city].filter(Boolean).join(' · ') || '—'} · {fmtDateTime(e.start_date)}</div>
                  </div>
                  <Badge variant="secondary" className="shrink-0 capitalize">{e.status || 'draft'}</Badge>
                </div>
              </Link>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* ---- Recent pending organizer applications (Organizer V1 — Phase 1) ---- */}
      <div className="grid grid-cols-1 mb-8">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base flex items-center gap-2"><Building2 className="h-4 w-4" /> Recent pending organizer applications</CardTitle>
            <Link to="/admin/organizers" className="text-xs text-primary hover:underline">View all</Link>
          </CardHeader>
          <CardContent className="space-y-2">
            {pendingOrgs.length === 0 && <p className="opacity-60 text-sm py-4 text-center">No pending organizer applications.</p>}
            {pendingOrgs.map((o) => (
              <Link key={o.id} to="/admin/organizers" className="block">
                <div className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2 hover:border-primary/50 transition-colors">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{o.organization_name || '(unnamed)'}</div>
                    <div className="text-xs opacity-70 truncate">{o.city || '—'} · submitted {fmtDateTime(o.submitted_at)}</div>
                  </div>
                  <Badge variant="outline" className="shrink-0">Pending</Badge>
                </div>
              </Link>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* ---- Data-quality warnings (only when present) ---- */}
      {warnings.length > 0 && (
        <Card className="border-amber-500/40">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" /> Data-quality warnings</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="list-disc pl-5 space-y-1 text-sm opacity-80">
              {warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
